/**
 * @fileoverview Input/output schemas for storylines-planner-v1.
 *
 * This agent takes a plot concept and expands it into storylines with beats
 * and story questions — the structural backbone of a story plan.
 *
 * It does NOT produce writtenPlot or suggestedCharacters; those are handled
 * by downstream agents (plot-planner, character-ideation) that consume
 * this agent's output.
 *
 * Output schema is flat (3 levels max) for Gemini API compatibility.
 * Beats and questions reference their parent storyline by index.
 * Consumer hooks are responsible for assembling the nested domain structure.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { StructurePhaseSchema, StorylinePriorityEnum } from '@/domains/story-planning/schemas';

// =============================================================================
// SOURCE MATERIAL SCHEMAS
// =============================================================================

/**
 * Schema for source materials as provided by UI/hooks (with type discriminator).
 */
export const SourceMaterialSchema = z.object({
  type: z.enum(['sequel', 'inspiration']).describe('Whether this is a sequel source or inspiration source.'),
  content: z.string().describe('The full text content of the source story.'),
});

export type SourceMaterial = z.infer<typeof SourceMaterialSchema>;

/**
 * Schema for source materials as passed to the flow (with pre-computed header).
 * This keeps logic in hooks, not in Handlebars templates.
 */
export const FlowSourceMaterialSchema = z.object({
  header: z.string().describe('Pre-computed header for this source material (e.g., "Original Story (for sequel/spin-off)").'),
  content: z.string().describe('The full text content of the source story.'),
});

export type FlowSourceMaterial = z.infer<typeof FlowSourceMaterialSchema>;

/**
 * Transforms a SourceMaterial to a FlowSourceMaterial by computing the header.
 */
export function transformSourceMaterial(material: SourceMaterial): FlowSourceMaterial {
  const header = material.type === 'sequel'
    ? 'Original Story (for sequel/spin-off)'
    : 'Inspiration Source';
  return { header, content: material.content };
}

// =============================================================================
// HOOK INPUT SCHEMA
// =============================================================================

export const HookInputSchema = z.object({
  userMessage: z.string().describe('User instructions including the plot concept to expand.'),
  sourceMaterials: z.array(SourceMaterialSchema).optional().describe('Source stories for sequel/inspiration-based plots.'),
});

export type HookInput = z.infer<typeof HookInputSchema>;

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.storyContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.loreContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.seriesContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.characters.schema)
  .merge(AGENT_SCHEMA_OBJECTS.worldElements.schema)
  .merge(AGENT_SCHEMA_OBJECTS.userMessage.schema)
  .merge(AGENT_SCHEMA_OBJECTS.planningGenreGuidance.schema)
  .merge(AGENT_SCHEMA_OBJECTS.bannedNames.schema)
  .merge(AGENT_SCHEMA_OBJECTS.projectedWordCount.schema)
  .merge(z.object({
    sourceMaterials: z.array(FlowSourceMaterialSchema).optional().describe('Source stories for sequel/inspiration-based plots with pre-computed headers.'),
  }));

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA (Flat structure for Gemini compatibility)
// =============================================================================

/**
 * Storyline metadata without nested beats or questions.
 * Beats and questions reference storylines by index.
 */
const StorylineOutputSchema = z.object({
  name: z.string().describe(
    'Short, evocative name for the storyline (3-5 words, at least 10 characters). ' +
    'A-storylines: Often named after the main goal ("The Ark Hunt", "Toppling the Regime"). ' +
    'B-storylines: Often named after relationships ("Indy and Marion", "Luke\'s Training"). ' +
    'C-storylines: Named after subplot focus ("The Ewok Alliance", "The Mentor\'s Secret").'
  ),
  summary: z.string().describe(
    'A brief description of the storyline (1-3 sentences). ' +
    'Captures how the storyline starts, develops, and resolves at a high level.'
  ),
  priority: StorylinePriorityEnum.describe(
    'Storyline priority following the A/B/C pattern. ' +
    'A = Main external plot with high stakes. B = Internal/relational subplot. C = Minor storyline for texture.'
  ),
});

/**
 * Storyline beat with storylineIndex reference.
 */
const StorylineBeatOutputSchema = z.object({
  storylineIndex: z.number().describe('Index of the storyline this beat belongs to (0-based).'),
  storylinePhase: StructurePhaseSchema.describe(
    'The structural function this beat serves for THIS STORYLINE\'s internal arc. ' +
    'A storyline\'s Inciting Incident might occur at the global Midpoint if that storyline starts late.'
  ),
  globalPhase: StructurePhaseSchema.describe(
    'Where this beat falls in the OVERALL story timeline, anchored by the A-Story. ' +
    'For A-storylines, globalPhase always equals storylinePhase. ' +
    'For B/C storylines, globalPhase may differ (e.g. a B-story\'s "Inciting Incident" at the global "Midpoint").'
  ),
  order: z.number().min(0).default(0).describe(
    'Ordering index (integer >= 0) when multiple beats share the same globalPhase (0-based). ' +
    'Use 0 for a single beat, 0/1/2/3 for multiple beats within one phase.'
  ),
  plotEvent: z.string().describe(
    'The concrete, observable event that occurs in the story world. ' +
    'Focus on what physically happens—actions, discoveries, confrontations, arrivals, departures. ' +
    'Examples: "The rebel base is destroyed", "Hero discovers the hidden map", "The villain escapes prison".'
  ),
  narrativeState: z.string().optional().describe(
    'The emotional or thematic state of this storyline thread at this moment (optional). ' +
    'How does this event affect the narrative stakes or emotional tenor? ' +
    'Examples: "Hope is lost", "The mystery deepens", "Trust is shattered".'
  ),
  goalProgress: z.number().min(-1).max(1).describe(
    'Progress toward the storyline\'s central objective at this moment. ' +
    '+1 = Major advancement toward the goal (victory, breakthrough). ' +
    '-1 = Significant setback or reversal (defeat, loss). ' +
    '0 = Neutral, lateral movement, or setup without clear progress.'
  ),
});

/**
 * Story question with storylineIndex reference.
 */
const StoryQuestionOutputSchema = z.object({
  storylineIndex: z.number().describe('Index of the storyline this question belongs to (0-based).'),
  question: z.string().describe('The dramatic question (e.g., "Will Marion forgive Indy?", "Can they reach the Ark before the Nazis?")'),
});

/**
 * Output schema for the storylines-planner-v1 agent.
 *
 * Produces ONLY the structural backbone: storylines, beats, and questions.
 * Does NOT produce writtenPlot or suggestedCharacters — those are handled
 * by separate downstream agents.
 *
 * Flat structure (3 levels max) for Gemini API compatibility:
 * - Level 1: Root object
 * - Level 2: storylines/beats/questions arrays
 * - Level 3: Array items
 *
 * Consumer hooks reassemble into nested Storyline entities with beats and questions.
 */
export const OutputSchema = z.object({
  // Story metadata
  title: z.string().describe('A compelling, evocative title for this story.'),
  summary: z.string().describe('A concise overview of the plot (100-150 words) that weaves together the story summary, its central theme, and the main conflict.'),

  // Storylines (flat, without beats)
  storylines: z.array(StorylineOutputSchema).describe(
    'Array of storylines following the A/B/C priority categories. ' +
    'Typically 3-6 storylines for novel-length fiction. ' +
    'Requires at least one A-storyline and one B-storyline. ' +
    'Multiple storylines per priority are encouraged when the story concept warrants it ' +
    '(e.g., 2A + 2B + 1C for an epic, or 1A + 1B for a focused thriller). ' +
    'C-storylines are optional for texture and world-building.'
  ),

  // Beats with storylineIndex references
  storylineBeats: z.array(StorylineBeatOutputSchema).describe(
    'All storyline beats with storylineIndex references. ' +
    'Each storyline should have beats at key structural phases (at minimum Initial State, Inciting Incident, Midpoint, Climax, and Final State). ' +
    'Beat count should match the story type — see prompt guidance.'
  ),

  // Story questions with storylineIndex references
  storyQuestions: z.array(StoryQuestionOutputSchema).describe(
    'Dramatic questions that keep readers engaged—raised early, answered later. ' +
    'Include 2-4 questions per storyline. Questions belong to specific storylines.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;

// Type exports for consumer convenience
export type StorylineOutput = z.infer<typeof StorylineOutputSchema>;
export type StorylineBeatOutput = z.infer<typeof StorylineBeatOutputSchema>;
export type StoryQuestionOutput = z.infer<typeof StoryQuestionOutputSchema>;
